/**
 * localStorage cache for the heavy 3-year time-series (ts04/ts08/ts15).
 *
 * Stale-while-revalidate: callers read cache to render instantly, then race a
 * fresh fetch in the background and overwrite when it lands. Old entries are
 * immutable historical days, so showing slightly stale data while we revalidate
 * is safe.
 *
 * Quota: ~270KB total for the three series. localStorage is 5MB+ per origin,
 * so we have plenty of headroom but still handle QuotaExceededError defensively.
 */

const PREFIX = 'tcs_ts_v1_';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // skip cache if older than 24h to bound staleness

const isStorageAvailable = () => {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
};

export const readCache = (key) => {
  if (!isStorageAvailable()) return null;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.data || !parsed.savedAt) {
      return null;
    }

    const ageMs = Date.now() - parsed.savedAt;
    if (ageMs > MAX_AGE_MS) return null;

    return { data: parsed.data, savedAt: parsed.savedAt, ageMs };
  } catch {
    return null;
  }
};

export const writeCache = (key, data) => {
  if (!isStorageAvailable() || !data || typeof data !== 'object') return false;

  const payload = JSON.stringify({ data, savedAt: Date.now() });
  try {
    localStorage.setItem(PREFIX + key, payload);
    return true;
  } catch (err) {
    // QuotaExceededError or storage disabled. Try once to clear our entries and retry.
    const isQuota = err && (err.name === 'QuotaExceededError' || err.code === 22);
    if (isQuota) {
      console.warn(`[timeSeriesCache] Quota exceeded for ${key}, evicting stored series`);
      try {
        Object.keys(localStorage)
          .filter(k => k.startsWith(PREFIX))
          .forEach(k => localStorage.removeItem(k));
        localStorage.setItem(PREFIX + key, payload);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }
};
